import { Type } from '@sinclair/typebox';
import { FormattedPriceListSchema } from '@stickerapp-org/emporio-api-contract';
import { cartCommandResponseSchema } from "../commands/index.mjs";
import { priceMajorMinorInclOnlySchema, priceMajorMinorSimpleSchema, priceMajorMinorSchema, shippingMethodPriceSchema } from "../price.mjs";
import { cartCheckoutSchema } from "./checkout.mjs";
import { cartCustomerSchema } from "./customer.mjs";
import { totalAmountForFreeShippingMajorMinor } from "./shipping.mjs";
export const cartAttributes = Type.Record(Type.String(), Type.Unknown());
const stickerLineItemContentBase = Type.Object({
    // These are stored on the CustomSticker object as well, but we need to store them here as well
    // in order to calculate prices without having to also load the CustomSticker object
    productFamilyName: Type.String(),
    productName: Type.String(),
    attributes: cartAttributes,
    priceList: Type.Object({ prices: FormattedPriceListSchema }),
});
export const cartLineCustomStickerContent = Type.Intersect([
    stickerLineItemContentBase,
    Type.Object({
        type: Type.Literal('customSticker'),
        customStickerId: Type.String(),
        customStickerVersion: Type.String(),
        fromStickerShopItemId: Type.Optional(Type.Number()),
        name: Type.String(),
        imageUrl: Type.Optional(Type.String()),
    }),
]);
export const cartLineStickerPackContent = Type.Intersect([
    stickerLineItemContentBase,
    Type.Object({
        type: Type.Literal('stickerPack'),
        stickerPackId: Type.Number(),
        name: Type.String(),
        imageUrl: Type.String(),
    }),
]);
export const cartLineGiftCardContent = Type.Object({
    type: Type.Literal('giftCard'),
    name: Type.String(),
    imageUrl: Type.String(),
    amount: Type.Object({
        major: Type.Number(),
        minor: Type.Number(),
    }),
    figureId: Type.String(),
});
export const cartLineContent = Type.Union([cartLineCustomStickerContent, cartLineGiftCardContent, cartLineStickerPackContent]);
export const cartLineItemPriceSchema = Type.Object({
    /**
     * This represents the effective price per unit/quantity including discounts.
     */
    unit: priceMajorMinorSchema,
    /**
     * If this is set, it means that the price has been lowered by a discount or similar.
     * Subtract this from the unit price to get the savings per unit.
     */
    unitOriginal: Type.Optional(priceMajorMinorSchema),
    /**
     * This represents the effective total price (all units/quantity) for the line item including discounts.
     */
    total: priceMajorMinorSchema,
    /**
     * If this is set, it means that the price has been overridden by a discount or similar.
     * Subtract this from the total price to get the total savings for a line item.
     */
    totalOriginal: Type.Optional(priceMajorMinorSchema),
    /**
     * Total discount amount for the line item.
     */
    discount: Type.Optional(priceMajorMinorInclOnlySchema),
    /**
     * Total bundle saving for the line item.
     */
    bundleSavings: Type.Optional(priceMajorMinorSimpleSchema),
});
const cartAndOrderLineSchema = Type.Object({
    id: Type.String(),
    createdAt: Type.String(),
    updatedAt: Type.Optional(Type.String()),
    quantity: Type.Number(),
    content: cartLineContent,
});
export const cartLineSchema = Type.Intersect([
    cartAndOrderLineSchema,
    Type.Object({
        price: cartLineItemPriceSchema,
    }),
]);
const atlasLineSchema = Type.Intersect([
    cartAndOrderLineSchema,
    Type.Object({
        price: Type.Intersect([
            cartLineItemPriceSchema,
            Type.Object({
                /**
                 * Total giftcard amount for the line item.
                 */
                giftCard: Type.Optional(Type.Number()),
            }),
        ]),
    }),
]);
const giftCardPaymentSchema = Type.Object({
    amount: Type.Object({
        major: Type.Number(),
        minor: Type.Number(),
    }),
    amountToUse: Type.Object({
        major: Type.Number(),
        minor: Type.Number(),
    }),
    amountRemaining: Type.Object({
        major: Type.Number(),
        minor: Type.Number(),
    }),
    code: Type.String(),
});
const giftCardPaymentAtlasSchema = Type.Object({
    amount: Type.Number(),
    amountToUse: Type.Number(),
    amountRemaining: Type.Number(),
    code: Type.String(),
});
export const discountCodeSchema = Type.Intersect([
    Type.Object({
        code: Type.String(),
    }),
    Type.Object({
        amount: priceMajorMinorSimpleSchema,
    }),
]);
export const currentCartVersionSchema = Type.Literal('current');
export const taxJarRateSchema = Type.Object({
    amountToCollect: Type.Number(),
    rate: Type.Number(),
    freightTaxable: Type.Boolean(),
});
const taxTypeSchema = Type.Union([Type.Literal('vat'), Type.Literal('salesTax')]);
export const cartFeesSchema = Type.Object({
    /**
     * This is the same as the total price for the selected shipping method.
     * It exists here to be able to show the shipping method price in the cart
     * without having to expand the checkout data.
     */
    shipping: Type.Optional(shippingMethodPriceSchema),
});
export const taxSchema = Type.Object({
    amountTotal: priceMajorMinorSimpleSchema,
    type: taxTypeSchema,
    isTaxExempt: Type.Union([Type.Boolean(), Type.Null()]),
});
export const cartSchema = Type.Object({
    id: Type.String(),
    version: Type.String(),
    parentVersion: Type.Optional(Type.String()),
    createdAt: Type.String(),
    updatedAt: Type.Optional(Type.String()),
    lines: Type.Array(cartLineSchema),
    giftCards: Type.Array(giftCardPaymentSchema),
    discountCodes: Type.Array(discountCodeSchema),
    checkout: Type.Optional(cartCheckoutSchema),
    customer: cartCustomerSchema,
    comment: Type.Optional(Type.String()),
    factoryId: Type.Optional(Type.Number()),
    fees: cartFeesSchema,
    tax: Type.Optional(taxSchema),
    leftToFreeShipping: Type.Union([priceMajorMinorSimpleSchema, Type.Null()]),
    bundleSavings: Type.Union([priceMajorMinorSimpleSchema, Type.Null()]),
    totalAmountForFreeShipping: Type.Optional(totalAmountForFreeShippingMajorMinor),
    /**
     * Taxjar combined taxrate object
     */
    taxjar: Type.Optional(taxJarRateSchema),
    countryCode: Type.String(),
    language: Type.String(),
    validCountryCode: Type.Boolean(),
    validCurrency: Type.Boolean(),
    currency: Type.String(),
    ipAddress: Type.String(),
    /** Eg stickerapp.com or stickerapp.se, the domain of the site where the cart is created */
    siteUrl: Type.String(),
    /** The full url to the checkout page, eg https://stickerapp.com/checkout */
    checkoutPageUrl: Type.String(),
    /** Indicates whether local duties or other shipping fees may apply to the cart */
    isExport: Type.Boolean(),
});
export const cartUpdateResponseSchema = Type.Object({
    commands: Type.Array(cartCommandResponseSchema),
    cart: cartSchema,
});
export const cartResponseSchema = Type.Object({
    cart: cartSchema,
});
export const myCartsResponseSchema = Type.Object({
    carts: Type.Array(Type.Object({ cartId: Type.String() })),
});
//# sourceMappingURL=cart.js.map